import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { CheckElasticConnection } from "../Authentication/permissionsSlice";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import { getSessionAuthHeader, withSessionFields } from "../../../Common/sessionAuth";
import {
  ALL_PRODUCTS_LIST_WITH_VARIANTS,
  BASE_URL,
  GET_STORES,
  PRODUCTS_LIST,
  LOTTERY_PRODUCT_LIST,
  ELASTIC_SEARCH_BASE_URL,
  PRODUCTS_LIST_NEW,
  UPDATE_TYPE,
  PRODUCT_SALES_HISTORY,
  PRODUCT_SALES_HISTORY_COUNT,
  Product_SALES_HISTORY,
  BASE_NODE_URL,
} from "../../../Constants/Config";
import { ciBase, ciUrl } from "../../../Constants/nodeCi";

const initialState = {
  loading: false,
  productsData: [],
  productStatus: null,
  allProductsData: [],
  page: 0,
  offset: 0,
  limit: 10,
  hasMore: true,
  successMessage: "",
  error: "",
  formData: [],

  // for add product varient
  isLoading: false,
  isError: false,
  // for edit product
  isEditError: false,
  // for fetchDataBy Id
  isFetchLoading: false,

  // showType initial state
  showType: 3,

  varientProduct: [],
  fetchProductLoadingDropdown: false,
  fetchCategoryListLoading: false,
  fetchTaxListLoading: false,

  salesHistoryLoading: true,
  salesHistoryData: [],
  salesHistoryError: false,
  salesHistoryCountLoading: true,
  salesHistoryCount: true,

  instancePoLoading: false,
  instancePoData: [],
  instancePoError: false,

  totalProductCount: 0,

  titleCheckLoading: false,
  checkUpcCodeSingleLoading: false,

  changeOrderMethodLoading: false,
  titleCheckLoading: false,
  checkUpcCodeSingleLoading: false,

  // delete product
  deleteProductLoading: false,
  deleteProductSuccess: false,
  deleteProductError: false,

  // delete multiple checkbox product
  deleteMultipleProductLoading: false,
  deleteMultipleProductSuccess: false,
  deleteMultipleProductError: false,
};
let cancelTokenSource;
let activeRequests = 0;
let IS_ELASTIC_CONNECTED = false;

// Generate pening , fulfilled and rejected action type
export const fetchAllProducts = createAsyncThunk(
  "products/fetchAllProducts",
  async (data) => {
    try {
      const { token, ...dataNew } = data;
      const response = await axios.post(
        BASE_URL + ALL_PRODUCTS_LIST_WITH_VARIANTS,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        return response.data.result;
      }
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

// Generate pening , fulfilled and rejected action type
export const fetchProductsData = createAsyncThunk(
  "products/fetchProductsData",
  async (data, { getState, dispatch, rejectWithValue }) => {
    // Cancel the previous request if it exists
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Previous request canceled");
    }

    // Create a new cancel token
    cancelTokenSource = axios.CancelToken.source();
    try {
      const currentUrl = window.location.pathname;
      let split = currentUrl.split("/");
      let isElasticConnected = getState().permissions.isElasticConnected;
      // Elastic status starts as `null` and is only resolved by
      // CheckElasticConnection on SideMenu mount. On a fresh load the first
      // product fetch can race ahead of that check and see `null`, wrongly
      // falling back to BASE_URL. When still undetermined, resolve it now.
      if (isElasticConnected === null) {
        await dispatch(CheckElasticConnection());
        isElasticConnected = getState().permissions.isElasticConnected;
      }

      // Lottery pages must read products from the DB (api-ci or Node /ci), never elastic.
      let baseurl =
        isElasticConnected && !currentUrl.includes("/inventory/lottery")
          ? ELASTIC_SEARCH_BASE_URL
          : ciBase(PRODUCTS_LIST);
      const { token, ...dataNew } = data;
      const response = await axios.post(
        baseurl + PRODUCTS_LIST,
        //BASE_URL + PRODUCTS_LIST,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          cancelToken: cancelTokenSource.token,
        }
      );
      // console.log("api products: ", response.data);
      let arr = [];
      let status = false;
      if (response.status === 200 && response?.data?.length) {
        arr = response?.data;
        status = true;
        return { arr, status, type: data.type || "" };
      } else if (response.status === 200 && !response?.data?.length) {
        arr = [];
        status = false;
        return { arr, status, type: data.type || "" };
      }
    } catch (error) {
      // throw new Error("Internal Server Error");
      if (axios.isCancel(error)) {
        console.log("Request canceled:", error.message);
      } else {
        const customError = {
          message: toThunkRejectValue(error).message,
          status: toThunkRejectValue(error).status,
          data: error.response ? error.response.data : null,
        };
        return rejectWithValue(customError);
      }
    }
  }
);

// Lottery list — dedicated endpoint. Body carries the auth set (merchant_id,
// token_id, login_type, session_id); `token` is stripped into the auth header.
// Returns the full lottery product list in one call; replaces the heavy
// Products_list fetch on /inventory/lottery.
export const fetchLotteryProducts = createAsyncThunk(
  "products/fetchLotteryProducts",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + LOTTERY_PRODUCT_LIST,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Response shape can be a bare array or { data: [...] } / { result: [...] }.
      const body = response?.data;
      const arr = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.result)
            ? body.result
            : [];
      return { arr, status: arr.length > 0 };
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const updateProductsType = createAsyncThunk(
  "products/updateProductsType",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(ciUrl(UPDATE_TYPE), dataNew, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      // console.log(response)
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);
export const getInventorySetting = createAsyncThunk(
  "products/getInventorySetting",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Profile_setup/inventory_register_setting"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response?.data?.result?.cost_per;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const getInventorySettingOnVarient = createAsyncThunk(
  "products/getInventorySettingOnVarient",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Profile_setup/inventory_register_setting"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const editProductData = createAsyncThunk(
  "products/editProduct",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/edit_produt"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response;
    } catch (error) {
      console.log("error api", error);
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const addProduct = createAsyncThunk(
  "products/addProduct",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/add_product"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// dropdown content
export const fetchVarientList = createAsyncThunk(
  "products/fetchVarientList",
  async (payload, { rejectWithValue, signal }) => {
    const { token, ...newData } = payload;
    try {
      const body = {
        ...newData,
        limit: newData.limit ?? 30,
        offset: newData.offset ?? 0,
        search: newData.search ?? "",
      };
      const response = await axios.post(
        ciUrl("Varient_react_api/varients_list"),
        body,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchCategoryList = createAsyncThunk(
  "products/fetchCategoryList",
  async (payload, { rejectWithValue, signal }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Categoryapi/category_list"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchTaxList = createAsyncThunk(
  "products/fetchTaxList",
  async (payload, { signal }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Settingapi/tax_list"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const fetchProductList = createAsyncThunk(
  "products/fetchProductList",
  async (payload, { rejectWithValue, signal }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Productapi/products_list_dropdown"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchProductsDataById = createAsyncThunk(
  "products/fetchProductData",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        ciUrl("Product_api_react/get_productdata_ById"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      //  catch (error) {
      //   throw new Error(error.response.data.message);
      // }
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const checkProductTitle = createAsyncThunk(
  "products/checkProductTitle",
  async (payload) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/check_productTitle"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const fetchVendorList = createAsyncThunk(
  "products/fetchVendorList",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/product_vendors_list"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const filterVendorAPI = createAsyncThunk(
  "products/filterVendorAPI",
  async (payload) => {
    try {
      const response = await axios.post(
        BASE_URL + "Vendor_api/vendor_list",
        withSessionFields(payload),
        { headers: getSessionAuthHeader() }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const assignProductVendor = createAsyncThunk(
  "products/assignProductVendor",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        ciUrl("Product_api_react/assign_product_vendors"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchSalesHistory = createAsyncThunk(
  "products/fetchSalesHistory",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl(PRODUCT_SALES_HISTORY),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response?.data?.status) {
        return response?.data;
      }
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);
export const fetchSalesHistoryCount = createAsyncThunk(
  "products/fetchSalesHistoryCount",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl(PRODUCT_SALES_HISTORY_COUNT),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response?.data?.status) {
        return response?.data;
      }
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);
export const assignPrefferedVendor = createAsyncThunk(
  "products/assignPrefferedVendor",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/assign_preferred_vendor"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const deleteProductVendor = createAsyncThunk(
  "products/deleteProductVendor",
  async (payload) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/delete_product_vendor"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const saveVendorList = createAsyncThunk(
  "products/saveVendorList",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        ciUrl("Product_api_react/save_vendor_list"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const getAlreadyAssignVendor = createAsyncThunk(
  "products/getAlreadyAssignVendor",
  async (payload) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        ciUrl("Product_api_react/assign_product_vendors_list"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const bulkVendorAssign = createAsyncThunk(
  "products/bulkVendorAssign",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/bulk_assign_vendors"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const saveSingleVarientPO = createAsyncThunk(
  "products/saveSingleVarientPO",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        ciUrl("Product_api_react/save_instant_po"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const saveBulkInstantPo = createAsyncThunk(
  "products/saveBulkInstantPo",
  async (payload) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        ciUrl("Product_api_react/bulk_instant_po"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const changeOnlineOrderMethod = createAsyncThunk(
  "products/changeOnlineOrderMethod",
  async (payload) => {
    const { token, ...payloadNew } = payload;
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/product_show_status_update"),
        payloadNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const changeShowStatusProduct = createAsyncThunk(
  "products/changeShowStatusProduct",
  async (payload) => {
    const { token, ...payloadNew } = payload;
    try {
      const response = await axios.post(
        BASE_URL + "Product_api_react/update_show_status",
        payloadNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const deleteProductAPI = createAsyncThunk(
  "products/deleteProductAPI",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/delete_product"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const checkUpcCodeMultiple = createAsyncThunk(
  "products/checkUpcCodeMultiple",
  async (payload) => {
    // const token = payload.get('token'); // Extract the token from FormData
    // payload.delete('token');
    const { token, ...data } = payload;
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/check_upc"),
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const checkUpcCodeSingle = createAsyncThunk(
  "products/checkUpcCodeSingle",
  async (payload) => {
    // const token = payload.get('token'); // Extract the token from FormData
    // payload.delete('token');
    const { token, ...data } = payload;
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/check_upc_form"),
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const checkUpcOnVarientEdit = createAsyncThunk(
  "products/checkUpcOnVarientEdit",
  async (payload) => {
    // const token = payload.get('token'); // Extract the token from FormData
    // payload.delete('token');
    const { token, ...newData } = payload;
    try {
      const response = await axios.post(
        BASE_URL + "Product_api_react/check_upc_new",
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const checkCustomCodeUniqueness = createAsyncThunk(
  "products/checkCustomCodeUniqueness",
  async (payload) => {
    const { token, ...data } = payload;
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/check_custom_code"),
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }
);

export const updateEditVarient = createAsyncThunk(
  "products/updateEditVarient",
  async (payload, { rejectWithValue }) => {
    // const token = payload.get('token'); // Extract the token from FormData
    // payload.delete('token');
    const { token, ...newData } = payload;
    try {
      const response = await axios.post(
        BASE_URL + "Product_api_react/update_variant",
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchVarietDataById = createAsyncThunk(
  "products/fetchVarietDataById",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        BASE_URL + "Product_api_react/get_variantdata_ById",
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchTags = createAsyncThunk(
  "products/fetchTags",
  async (payload, { rejectWithValue, signal }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        BASE_URL + "Product_api_react/list_brand_tag",
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchBrands = createAsyncThunk(
  "products/fetchBrands",
  async (payload, { rejectWithValue, signal }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(
        BASE_URL + "Product_api_react/list_brand_tag",
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const getStores = createAsyncThunk(
  "products/getStores",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");

    try {
      const response = await axios.post(BASE_URL + GET_STORES, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const updateVariantMerging = createAsyncThunk(
  "products/updateVariantMerging",
  async (payload, { rejectWithValue }) => {
    // const token = payload.get('token'); // Extract the token from FormData
    // payload.delete('token');
    const { token, ...newData } = payload;
    try {
      const response = await axios.post(
        BASE_URL + "/api/Product_merge/merge",
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const deleteMultipleProductAPI = createAsyncThunk(
  "products/deleteMultipleProductAPI",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        ciUrl("Product_api_react/delete_multiple_products"),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchProductBulkInstantPoData = createAsyncThunk(
  "products/fetchProductBulkInstantPoData",
  async (payload, { rejectWithValue }) => {
    const token = payload.get("token"); // Extract the token from FormData
    payload.delete("token");
    try {
      const response = await axios.post(
        BASE_URL + "Last_five_po_api/last_po_data_api",
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchProductTotalCOunt = createAsyncThunk(
  "products/fetchProductTotalCOunt",
  async (payload, { rejectWithValue }) => {
    // const token = payload.get("token"); // Extract the token from FormData
    // payload.delete("token");
    try {
      const response = await axios.post(
        // ELASTIC_SEARCH_BASE_URL + "product_api_react/products_list_count",
        BASE_URL + "product_api_react/products_list_count",
        withSessionFields(payload),
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getSessionAuthHeader(),
          },
        }
      );
      return response?.data;
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    emptyProduct: (state, action) => {
      // console.log("empty action payload: ", action.payload);
      state.productsData = action.payload;
      state.offset = 0;
      state.limit = 10;
      state.hasMore = true;
      state.page = 0;
    },
    editProduct: (state, action) => {
      state.productsData = state.productsData.map((product) => {
        if (product.id === action.payload.id) {
          return {
            ...product, // Spread syntax to copy existing properties
            title: action.payload.title, // Update the title
            old_title: action.payload.title,
          };
        } else {
          // This isn't the one we're looking for - leave it as is
          return product;
        }
      });
    },
    updateFormValue: (state, action) => {
      state.formData = action?.payload;
    },
    changeShowType: (state, action) => {
      const { id, updateValue } = action.payload;
      const obj = state.productsData?.find((obj) => obj.id === id);
      if (obj) {
        obj.show_type = updateValue;
      }
    },
    changeShowStatus: (state, action) => {
      const { id, showStatus } = action.payload;
      const obj = state.productsData?.find((obj) => obj.id === id);
      if (obj) {
        obj.show_status = showStatus;
      }
    },
    setVarientList: (state, action) => {
      state.varientProduct = action.payload;
    },
    startScrollLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllProducts.pending, (state) => {
      state.loading = true;
      state.allProductsData = [];
    });
    builder.addCase(fetchAllProducts.fulfilled, (state, action) => {
      state.allProductsData = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAllProducts.rejected, (state, action) => {
      state.allProductsData = [];
      state.loading = false;
    });

    builder.addCase(fetchProductsData.pending, (state, action) => {
      console.log("fetchProductsData pending...", action);
      if (action?.meta?.arg?.type === "forPaginationLoadMoreProducts") return;

      activeRequests++;
      state.loading = true;
    });
    builder.addCase(fetchProductsData.fulfilled, (state, action) => {
      console.log("fetchProductsData fulfilled...", action);
      if (action?.meta?.arg?.type === "forPaginationLoadMoreProducts") return;

      activeRequests--;
      if (activeRequests === 0) {
        state.loading = false;
      }

      // state.productsData = action.payload;
      // state.productsData = [...state.productsData, ...action.payload];
      // Ensure productsData is always treated as an array
      if (!Array.isArray(state.productsData)) {
        state.productsData = [];
      }
      // Append new items to the productsData array
      // console.log(state);
      // const productIds = new Set(
      //   state.productsData.map((product) => product.title)
      // );

      // console.log("action.payload: ", action.payload);
      // console.log("product ids: ", productIds);

      // Append new items to the productsData array if they are not already present
      // action?.payload?.forEach((product) => {
      //   if (!productIds.has(product.title)) {
      //     state.productsData.push(product);
      //     productIds.add(product.title);
      //   }
      // });
      // JSON.parse(localStorage.getItem("product-focus-data")) &&
      // JSON.parse(localStorage.getItem("product-focus-data"))?.offset
      //   ? (state.offset =
      //       JSON.parse(localStorage.getItem("product-focus-data"))?.offset + 10)
      //   : (state.offset += 10);
      state.productStatus = action.payload?.status;
      state.productsData = [...state.productsData, ...action.payload?.arr];
      if (action.payload?.arr?.length > 0) {
        state.offset += 10; // Increment only if there are new products
      } else {
        state.hasMore = false;
      }

      // state.hasMore = state.totalProductCount > state.productsData?.length ? ;
      state.error = "";
    });
    builder.addCase(fetchProductsData.rejected, (state, action) => {
      console.log("fetchProductsData rejected...", action);
      if (action?.meta?.arg?.type === "forPaginationLoadMoreProducts") return;

      activeRequests--;
      state.productStatus = true;
      if (activeRequests === 0) {
        state.loading = false;
        state.productsData = [];
        state.error = action.error.message;
      }
      state.hasMore = false;
    });
    // Lottery list — single-call replace (no pagination append).
    builder.addCase(fetchLotteryProducts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchLotteryProducts.fulfilled, (state, action) => {
      state.loading = false;
      state.productStatus = action.payload?.status;
      state.productsData = action.payload?.arr || [];
      state.error = "";
    });
    builder.addCase(fetchLotteryProducts.rejected, (state, action) => {
      state.loading = false;
      state.productsData = [];
      state.error = action.error.message;
    });
    builder.addCase(updateProductsType.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProductsType.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updateProductsType.rejected, (state, action) => {
      state.loading = false;
      state.productsData = {};
      state.error = action.error.message;
    });

    // add product varient
    builder.addCase(addProduct.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(addProduct.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isError = false;
    });
    builder.addCase(addProduct.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });

    // edit product
    builder.addCase(editProductData.pending, (state) => {
      state.isLoading = true;
      state.isEditError = false;
    });
    builder.addCase(editProductData.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isEditError = false;
    });
    builder.addCase(editProductData.rejected, (state, action) => {
      state.isLoading = false;
      state.isEditError = true;
    });

    // fetchingDataById
    builder.addCase(fetchProductsDataById.pending, (state) => {
      state.isFetchLoading = true;
      // state.isEditError = false;
    });
    builder.addCase(fetchProductsDataById.fulfilled, (state, action) => {
      state.isFetchLoading = false;
      // state.isEditError = false;
    });
    builder.addCase(fetchProductsDataById.rejected, (state, action) => {
      state.isFetchLoading = false;
      // state.isEditError = true;
    });

    // product fetch listing in dropdown
    builder.addCase(fetchProductList.pending, (state) => {
      state.fetchProductLoadingDropdown = true;
      // state.isEditError = false;
    });
    builder.addCase(fetchProductList.fulfilled, (state, action) => {
      state.fetchProductLoadingDropdown = false;
      // state.isEditError = false;
    });
    builder.addCase(fetchProductList.rejected, (state, action) => {
      state.fetchProductLoadingDropdown = false;
      // state.isEditError = true;
    });

    // fetchCategoryList for dropdown options
    builder.addCase(fetchCategoryList.pending, (state) => {
      state.fetchCategoryListLoading = true;
      // state.isEditError = false;
    });
    builder.addCase(fetchCategoryList.fulfilled, (state, action) => {
      state.fetchCategoryListLoading = false;
      // state.isEditError = false;
    });
    builder.addCase(fetchCategoryList.rejected, (state, action) => {
      state.fetchCategoryListLoading = false;
      // state.isEditError = true;
    });

    // fetchTaxList for dropdown options
    builder.addCase(fetchTaxList.pending, (state) => {
      state.fetchTaxListLoading = true;
      // state.isEditError = false;
    });
    builder.addCase(fetchTaxList.fulfilled, (state, action) => {
      state.fetchTaxListLoading = false;
      // state.isEditError = false;
    });
    builder.addCase(fetchTaxList.rejected, (state, action) => {
      state.fetchTaxListLoading = false;
      // state.isEditError = true;
    });

    // fetchProductBulkInstantPoData

    builder.addCase(fetchProductBulkInstantPoData.pending, (state) => {
      state.instancePoLoading = true;
      state.instancePoData = [];
      state.instancePoError = false;
    });
    builder.addCase(
      fetchProductBulkInstantPoData.fulfilled,
      (state, action) => {
        state.instancePoLoading = false;
        state.instancePoData = action.payload;
        state.instancePoError = false;
      }
    );
    builder.addCase(fetchProductBulkInstantPoData.rejected, (state, action) => {
      state.instancePoLoading = false;
      state.instancePoData = [];
      state.instancePoError = true;
    });

    builder.addCase(fetchSalesHistory.pending, (state) => {
      state.salesHistoryLoading = true;
      state.salesHistoryData = [];
      state.salesHistoryError = false;
    });
    builder.addCase(fetchSalesHistory.fulfilled, (state, action) => {
      state.salesHistoryLoading = false;
      state.salesHistoryData = action.payload;
      state.salesHistoryError = false;
    });
    builder.addCase(fetchSalesHistory.rejected, (state, action) => {
      state.salesHistoryLoading = false;
      state.salesHistoryData = [];
      state.salesHistoryError = true;
    });
    builder.addCase(fetchSalesHistoryCount.pending, (state) => {
      state.salesHistoryCountLoading = true;
      state.salesHistoryCount = [];
    });
    builder.addCase(fetchSalesHistoryCount.fulfilled, (state, action) => {
      state.salesHistoryCountLoading = false;
      state.salesHistoryCount = action.payload;
    });
    builder.addCase(fetchSalesHistoryCount.rejected, (state, action) => {
      state.salesHistoryCountLoading = false;
      state.salesHistoryCount = [];
    });

    // changeOnlineOrderMethod
    builder.addCase(changeOnlineOrderMethod.pending, (state) => {
      state.changeOrderMethodLoading = true;
    });
    builder.addCase(changeOnlineOrderMethod.fulfilled, (state, action) => {
      state.changeOrderMethodLoading = false;
    });
    builder.addCase(changeOnlineOrderMethod.rejected, (state, action) => {
      state.changeOrderMethodLoading = false;
    });

    // fetchProductTotalCOunt

    builder.addCase(fetchProductTotalCOunt.pending, (state) => {
      state.totalProductCount = 0;
    });
    builder.addCase(fetchProductTotalCOunt.fulfilled, (state, action) => {
      state.totalProductCount = action.payload.count;
    });
    builder.addCase(fetchProductTotalCOunt.rejected, (state, action) => {
      state.totalProductCount = 0;
    });

    // check product title status loading

    builder.addCase(checkProductTitle.pending, (state) => {
      state.titleCheckLoading = true;
    });
    builder.addCase(checkProductTitle.fulfilled, (state, action) => {
      state.titleCheckLoading = false;
    });
    builder.addCase(checkProductTitle.rejected, (state, action) => {
      state.titleCheckLoading = false;
    });

    // check product upc code status loading
    builder.addCase(checkUpcCodeSingle.pending, (state) => {
      state.checkUpcCodeSingleLoading = true;
    });
    builder.addCase(checkUpcCodeSingle.fulfilled, (state, action) => {
      state.checkUpcCodeSingleLoading = false;
    });
    builder.addCase(checkUpcCodeSingle.rejected, (state, action) => {
      state.checkUpcCodeSingleLoading = false;
    });

    // DELETE SINGLE PRODUCT API
    builder.addCase(deleteProductAPI.pending, (state) => {
      state.deleteProductLoading = true;
      state.deleteProductSuccess = false;
      state.deleteProductError = false;
    });
    builder.addCase(deleteProductAPI.fulfilled, (state, action) => {
      state.deleteProductLoading = false;
      state.deleteProductSuccess = action.payload;
      state.deleteProductError = false;
      if (action.payload?.status) {
        state.productsData = state.productsData.filter(
          (product) => +product.id !== +action.meta.arg.get("id")
        );
      }
    });
    builder.addCase(deleteProductAPI.rejected, (state, action) => {
      state.deleteProductLoading = false;
      state.deleteProductSuccess = false;
      state.deleteProductError = true;
    });

    // DELETE MULTIPLE CHECKBOX PRODUCT API
    builder.addCase(deleteMultipleProductAPI.pending, (state) => {
      state.deleteMultipleProductLoading = true;
      state.deleteMultipleProductSuccess = false;
      state.deleteMultipleProductError = false;
    });
    builder.addCase(deleteMultipleProductAPI.fulfilled, (state, action) => {
      state.deleteMultipleProductLoading = false;
      state.deleteMultipleProductSuccess = action.payload;
      state.deleteMultipleProductError = false;

      if (action.payload?.status === "success") {
        const ids = action.meta.arg.get("ids").split(",");

        state.productsData = state.productsData.filter(
          (product) => !ids.includes(product.id)
        );
      }
    });
    builder.addCase(deleteMultipleProductAPI.rejected, (state, action) => {
      state.deleteMultipleProductLoading = false;
      state.deleteMultipleProductSuccess = false;
      state.deleteMultipleProductError = true;
    });
  },
});

// fetchProductBulkInstantPoData

export const {
  editProduct,
  emptyProduct,
  updateFormValue,
  changeShowType,
  setVarientList,
  changeShowStatus,
  startScrollLoading,
} = productsSlice.actions;
export default productsSlice.reducer;
